import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import ProcessRoadmap from "./ProcessRoadmap";
import WorkAreaSurvey from "./WorkAreaSurvey";
import ExecutionFollowup from "./ExecutionFollowup";
import CoordinationMeetings from "./CoordinationMeetings";
import BruksskedeChecklists from "./BruksskedeChecklists";
import UsePhaseFollowup from "./UsePhaseFollowup";
import SafetyRounds from "./SafetyRounds";
import SafetyRules from "./SafetyRules";
import SamRoutine from "./SamRoutine";
import BasuRoutine from "./BasuRoutine";
import BasuChecklist from "./BasuChecklist";
import SafetyActivityPlan from "./SafetyActivityPlan";
import SitePresence from "./SitePresence";
import TemporaryStructures from "./TemporaryStructures";
import ToolboxTalks from "./ToolboxTalks";
import DisciplinaryActions from "./DisciplinaryActions";
import ByggherreHandling from "@/components/BasProcess/ByggherreHandling";
import DelegationOrder from "@/components/BasProcess/DelegationOrder";
import DelegationLetter from "@/components/BasProcess/DelegationLetter";
import ByggherreChecklist from "@/components/BasProcess/ByggherreChecklist";
import DutyAssignment from "@/components/BasProcess/DutyAssignment";
import ByggherreReport from "@/components/BasProcess/ByggherreReport";
import StagePlan from "./StagePlan";
import RoleHome from "./RoleHome";
import DesignerSubmissions from "./DesignerSubmissions";
import DesignerRoutine from "./DesignerRoutine";
import DesignerDelivery from "./DesignerDelivery";
import ConsolidatedRiskSummary from "./ConsolidatedRiskSummary";
import ConsolidatedWorkAreaSurvey from "./ConsolidatedWorkAreaSurvey";
import BaspInbox from "./BaspInbox";
import ContractorManagement from "./ContractorManagement";
import ProjectMembers from "./ProjectMembers";
import ProjectMessages from "./ProjectMessages";
import ProjectProgressPanel from "./ProjectProgressPanel";
import ReviewOverview from '@/components/BasProcess/ReviewOverview';
import WorkPrepClearance from '@/components/BasProcess/WorkPrepClearance';
import SafetyDataSheetRegister from '@/components/BasProcess/SafetyDataSheetRegister';
import ProjectNextStep from "./ProjectNextStep";
import AmpGateBanner from "./AmpGateBanner";
import PriorNotification from "./PriorNotification";
import ProjectStages from "./ProjectStages";
import ProgramOverview from "./ProgramOverview";
import LanguageDeliveries from "./LanguageDeliveries";
import PhaseManager from "./PhaseManager";
import ProcurementRequirements from "./ProcurementRequirements";
import ScheduleReview from "./ScheduleReview";
import ThirdPartyPlan from "./ThirdPartyPlan";
import DeadlinesPanel from "./DeadlinesPanel";
import EquipmentInspections from "./EquipmentInspections";
import StatusSnapshot from "./StatusSnapshot";
import HazmatSurvey from "./HazmatSurvey";
import ElectricalIsolations from "./ElectricalIsolations";
import HotWorkPermits from "./HotWorkPermits";
import TrafficPlans from "./TrafficPlans";
import UtilityOutagePlans from "./UtilityOutagePlans";
import AcknowledgementTracking from "./AcknowledgementTracking";
import SafetyInductions from "./SafetyInductions";
import ExpiryWatch from "./ExpiryWatch";
import FinalDocumentation from "./FinalDocumentation";
import ProjectTimeline from "./ProjectTimeline";
import ScenarioAudit from "./ScenarioAudit";
import useProjectRole from "@/hooks/useProjectRole";
import ProcessIntroCard from "./ProcessIntroCard";
import useSimpleMode from "@/hooks/useSimpleMode";
import DetailLevelPanel from "./DetailLevelPanel";
import CoveragePanel from "./CoveragePanel";

import { PROJECT_ROLES, ROLE_TABS, type ProjectRole } from "@/lib/basProcess/roleViews";

import HandoverDocuments from "./HandoverDocuments";
import QaLog from "./QaLog";
import IncidentReports from "./IncidentReports";
import {
  Route,
  HardHat,
  Users,
  Building2,
  MapPinned,
  ShieldAlert,
  CalendarRange,
  FileSignature,
  MessagesSquare,
  AlertTriangle,
  Inbox,
  HardHat as HardHatIcon,
  ClipboardCheck,
  UserCircle,
  DraftingCompass,
  ShieldCheck,
  Network,
  Table2,
  Eye,
  LayoutGrid,
  BellRing,
  Layers,
  Wrench,
  History,
} from "lucide-react";

interface BasProcessViewProps {
  projectId: string;
  initialTab?: string;
}

type TabGroup = "forbered" | "planera" | "bygg" | "lamnaover";

const TAB_GROUPS: { key: TabGroup; label: string }[] = [
  { key: "forbered", label: "Förbered" },
  { key: "planera", label: "Planera" },
  { key: "bygg", label: "Bygg" },
  { key: "lamnaover", label: "Lämna över" },
];

const TAB_DEFS: { value: string; label: string; icon: React.ReactNode; group: TabGroup }[] = [
  { value: "minroll", label: "Min roll", icon: <UserCircle className="h-4 w-4" />, group: "forbered" },
  { value: "meddelanden", label: "Meddelanden", icon: <MessagesSquare className="h-4 w-4" />, group: "forbered" },
  { value: "inkorg", label: "Inkorg", icon: <Inbox className="h-4 w-4" />, group: "forbered" },
  { value: "oversikt", label: "Översikt", icon: <Route className="h-4 w-4" />, group: "forbered" },
  { value: "samrutin", label: "SAM-rutin", icon: <ClipboardCheck className="h-4 w-4" />, group: "forbered" },
  { value: "arbetsomrade", label: "Arbetsområde", icon: <MapPinned className="h-4 w-4" />, group: "planera" },
  {
    value: "projektorinlamning",
    label: "Projektörsinlämning",
    icon: <DraftingCompass className="h-4 w-4" />,
    group: "planera",
  },
  {
    value: "projektorrutin",
    label: "Projektörsrutin",
    icon: <DraftingCompass className="h-4 w-4" />,
    group: "forbered",
  },
  {
    value: "projektorleverans",
    label: "Projektörsleverans",
    icon: <DraftingCompass className="h-4 w-4" />,
    group: "lamnaover",
  },
  {
    value: "samordnadrisk",
    label: "Samordnad risksammanställning",
    icon: <Layers className="h-4 w-4" />,
    group: "planera",
  },
  {
    value: "samladarbetsomrade",
    label: "Samlad arbetsområdesutredning",
    icon: <Layers className="h-4 w-4" />,
    group: "planera",
  },
  { value: "forhandsanmalan", label: "Förhandsanmälan", icon: <BellRing className="h-4 w-4" />, group: "planera" },
  { value: "upphandling", label: "Upphandlingskrav", icon: <ClipboardCheck className="h-4 w-4" />, group: "planera" },
  { value: "tidplan", label: "Tidplansgranskning", icon: <CalendarRange className="h-4 w-4" />, group: "planera" },
  { value: "tredjeman", label: "Tredje man", icon: <Users className="h-4 w-4" />, group: "planera" },
  { value: "etapper", label: "Etapper", icon: <Layers className="h-4 w-4" />, group: "planera" },
  { value: "program", label: "Programöversikt", icon: <Layers className="h-4 w-4" />, group: "forbered" },
  { value: "sprak", label: "Språk", icon: <Users className="h-4 w-4" />, group: "bygg" },
  { value: "skeden", label: "Skeden", icon: <History className="h-4 w-4" />, group: "forbered" },
  { value: "farligtmaterial", label: "Farligt material", icon: <ShieldAlert className="h-4 w-4" />, group: "planera" },
  { value: "elsakerhet", label: "Frånkoppling el", icon: <Wrench className="h-4 w-4" />, group: "bygg" },
  { value: "skyddsregler", label: "Skyddsregler", icon: <ShieldAlert className="h-4 w-4" />, group: "planera" },
  { value: "hetaarbeten", label: "Heta arbeten", icon: <ShieldAlert className="h-4 w-4" />, group: "bygg" },
  { value: "taplaner", label: "TA-planer", icon: <CalendarRange className="h-4 w-4" />, group: "planera" },
  { value: "avstangningar", label: "Avstängningar media", icon: <Wrench className="h-4 w-4" />, group: "planera" },
  { value: "kvittenser", label: "Kvittens AMP", icon: <ClipboardCheck className="h-4 w-4" />, group: "bygg" },
  { value: "introduktion", label: "Säkerhetsintroduktion", icon: <ClipboardCheck className="h-4 w-4" />, group: "bygg" },
  { value: "bevakning", label: "Bevakning datum", icon: <History className="h-4 w-4" />, group: "bygg" },
  { value: "slutdok", label: "Slutdokumentation", icon: <FileSignature className="h-4 w-4" />, group: "lamnaover" },
  { value: "skedesplan", label: "Skedesplan", icon: <CalendarRange className="h-4 w-4" />, group: "planera" },
  { value: "utforande", label: "Utförande", icon: <HardHat className="h-4 w-4" />, group: "bygg" },
  { value: "entreprenorer", label: "Entreprenörer", icon: <HardHatIcon className="h-4 w-4" />, group: "bygg" },
  { value: "skyddsrond", label: "Skyddsrond", icon: <HardHat className="h-4 w-4" />, group: "bygg" },
  { value: "besiktningar", label: "Besiktningar", icon: <Wrench className="h-4 w-4" />, group: "bygg" },
  { value: "samordning", label: "Samordning", icon: <Users className="h-4 w-4" />, group: "bygg" },
  { value: "tillbud", label: "Tillbud/olyckor", icon: <AlertTriangle className="h-4 w-4" />, group: "bygg" },
  { value: "qalogg", label: "Q&A-logg", icon: <MessagesSquare className="h-4 w-4" />, group: "bygg" },
  { value: "granskning", label: "Granskningsöversikt", icon: <ClipboardCheck className="h-4 w-4" />, group: "bygg" },
  { value: "startbesked", label: "Startbesked arbetsberedningar", icon: <ClipboardCheck className="h-4 w-4" />, group: "bygg" },
  { value: "kemikalier", label: "Kemikalier och datablad", icon: <ShieldAlert className="h-4 w-4" />, group: "bygg" },
  { value: "scenarier", label: "Scenariogenomlysning", icon: <Route className="h-4 w-4" />, group: "forbered" },
  { value: "tidslinje", label: "Tidslinje", icon: <History className="h-4 w-4" />, group: "bygg" },
  { value: "dagslage", label: "Dagsläge", icon: <History className="h-4 w-4" />, group: "bygg" },
  { value: "overlamning", label: "Överlämning", icon: <FileSignature className="h-4 w-4" />, group: "lamnaover" },
  { value: "bruksskede", label: "Bruksskede", icon: <Building2 className="h-4 w-4" />, group: "lamnaover" },
  {
    value: "bruksuppfoljning",
    label: "Uppföljning bruksskede",
    icon: <ClipboardCheck className="h-4 w-4" />,
    group: "lamnaover",
  },
  { value: "basurutin", label: "Bas-U-rutin", icon: <ClipboardCheck className="h-4 w-4" />, group: "forbered" },
  { value: "basuchecklista", label: "Checklista Bas-U", icon: <ClipboardCheck className="h-4 w-4" />, group: "bygg" },
  { value: "skyddsverksamhet", label: "Skyddsverksamhet", icon: <ShieldCheck className="h-4 w-4" />, group: "planera" },
  { value: "platsnarvaro", label: "Platsnärvaro", icon: <MapPinned className="h-4 w-4" />, group: "forbered" },
  { value: "konstruktioner", label: "Tillfälliga konstruktioner", icon: <Layers className="h-4 w-4" />, group: "bygg" },
  { value: "toolbox", label: "Toolbox talk", icon: <MessagesSquare className="h-4 w-4" />, group: "bygg" },
  { value: "disciplin", label: "Disciplinära åtgärder", icon: <AlertTriangle className="h-4 w-4" />, group: "bygg" },
  { value: "byggherrehantering", label: "Byggherrens hantering", icon: <Building2 className="h-4 w-4" />, group: "forbered" },
  { value: "delegeringsordning", label: "Delegeringsordning", icon: <Network className="h-4 w-4" />, group: "forbered" },
  { value: "delegeringsbrev", label: "Delegeringsbrev", icon: <FileSignature className="h-4 w-4" />, group: "forbered" },
  { value: "byggherrechecklista", label: "Checklista byggherre", icon: <ShieldCheck className="h-4 w-4" />, group: "forbered" },
  { value: "uppgiftsfordelning", label: "Uppgiftsfördelning", icon: <Table2 className="h-4 w-4" />, group: "forbered" },
  { value: "byggherrerapport", label: "Byggherrerapport", icon: <FileSignature className="h-4 w-4" />, group: "forbered" },
];

export const BasProcessView: React.FC<BasProcessViewProps> = ({ projectId, initialTab }) => {
  const [tab, setTab] = useState(initialTab || "minroll");
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const { role, isOwner, canEdit, isReviewer, setPreviewRole } = useProjectRole(projectId);
  const simpleMode = useSimpleMode(projectId);

  const visibleTabs = useMemo(() => {
    const allowed = new Set(ROLE_TABS[role] ?? []);
    let base = showAll ? TAB_DEFS : TAB_DEFS.filter((t) => allowed.has(t.value));
    if (!showInactive) base = base.filter((t) => !simpleMode.hiddenTabs.has(t.value));
    // Se alltid till att den aktiva fliken går att nå.
    if (!base.some((t) => t.value === tab)) {
      const extra = TAB_DEFS.find((t) => t.value === tab);
      if (extra) return [...base, extra];
    }
    return base;
  }, [role, showAll, tab, showInactive, simpleMode.hiddenTabs]);

  const hiddenTabCount = useMemo(
    () => TAB_DEFS.filter((t) => simpleMode.hiddenTabs.has(t.value)).length,
    [simpleMode.hiddenTabs]
  );

  const openLink = (link: string) => {
    if (link === "arbetsomrade") navigate(`/arbetsomrade/${projectId}`);
    else if (link === "amp") navigate(`/uppdrag/${projectId}`);
    else if (link === "riskbedomning" || link === "arbetsberedning")
      navigate(`/uppdrag/${projectId}`);
    else if (TAB_DEFS.some((t) => t.value === link)) setTab(link);
  };

  return (
    <div className="space-y-4">
      <ProcessIntroCard />
      {isReviewer && (
        <div className="flex items-start gap-2 rounded-xl border border-border/70 bg-muted/40 p-3">
          <Eye className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Granskningsläge – {role}. </span>
            Du ser hela processen och kan läsa alla dokument och kommentera i meddelanden, men du
            kan inte ändra, kvittera eller lämna över.
          </p>
        </div>
      )}
      <ProjectNextStep projectId={projectId} onOpenLink={openLink} />
      <ProjectProgressPanel projectId={projectId} />
      <AmpGateBanner projectId={projectId} isOwner={isOwner} role={role} />
      <DetailLevelPanel projectId={projectId} onChanged={() => void simpleMode.reload()} />
      <DeadlinesPanel projectId={projectId} onOpenTab={setTab} />

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="gap-1">
          <UserCircle className="h-3 w-3" /> Din roll: {role}
        </Badge>
        {!canEdit && !isReviewer && (
          <Badge variant="outline" className="gap-1">
            <Eye className="h-3 w-3" /> Läsbehörighet
          </Badge>
        )}
        {isOwner && (
          <>
            <Badge className="gap-1">
              <ShieldCheck className="h-3 w-3" /> Projektägare
            </Badge>
            <Select value={role} onValueChange={(v) => setPreviewRole(v as ProjectRole)}>
              <SelectTrigger className="h-8 w-full sm:w-[190px]">
                <SelectValue placeholder="Visa som roll" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    Visa som {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        {hiddenTabCount > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setShowInactive((v) => !v)}>
            {showInactive ? "Dölj ej aktuella" : `Visa även ej aktuella (${hiddenTabCount})`}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
          <LayoutGrid className="mr-1 h-4 w-4" />
          {showAll ? "Visa bara min roll" : "Visa allt i processen"}
        </Button>
        {isOwner && (
          <Button
            variant={tab === "behorighet" ? "default" : "outline"}
            size="sm"
            onClick={() => setTab("behorighet")}
          >
            <ShieldCheck className="mr-1 h-4 w-4" /> Roller och behörigheter
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Mobil: en rullgardin i stället för radbruten flikrad */}
        <div className="sm:hidden">
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Välj del av processen" />
            </SelectTrigger>
            <SelectContent>
              {TAB_GROUPS.map((g) => {
                const items = visibleTabs.filter((t) => t.group === g.key);
                if (items.length === 0) return null;
                return (
                  <SelectGroup key={g.key}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {items.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: flikarna grupperade i klarspråk */}
        <TabsList className="hidden h-auto w-full flex-col items-stretch gap-2 bg-transparent p-0 sm:flex">
          {TAB_GROUPS.map((g) => {
            const items = visibleTabs.filter((t) => t.group === g.key);
            if (items.length === 0) return null;
            return (
              <div key={g.key} className="flex flex-wrap items-center gap-1">
                <span className="mr-1 w-20 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {g.label}
                </span>
                {items.map((t) => (
                  <TabsTrigger key={t.value} value={t.value} className="gap-2">
                    {t.icon} {t.label}
                  </TabsTrigger>
                ))}
              </div>
            );
          })}
        </TabsList>


        <TabsContent value="meddelanden" className="mt-4">
          <ProjectMessages projectId={projectId} />
        </TabsContent>
        <TabsContent value="minroll" className="mt-4">
          <RoleHome projectId={projectId} onOpenLink={openLink} />
        </TabsContent>
        <TabsContent value="inkorg" className="mt-4">
          <BaspInbox projectId={projectId} onOpenTab={setTab} />
        </TabsContent>
        <TabsContent value="oversikt" className="mt-4 space-y-4">
          <ProcessRoadmap projectId={projectId} onOpenLink={openLink} />
          <CoveragePanel projectId={projectId} onOpenLink={openLink} />
        </TabsContent>

        <TabsContent value="samrutin" className="mt-4">
          <SamRoutine projectId={projectId} />
        </TabsContent>
        <TabsContent value="basurutin" className="mt-4">
          <BasuRoutine projectId={projectId} />
        </TabsContent>
        <TabsContent value="basuchecklista" className="mt-4">
          <BasuChecklist projectId={projectId} />
        </TabsContent>
        <TabsContent value="skyddsverksamhet" className="mt-4">
          <SafetyActivityPlan projectId={projectId} />
        </TabsContent>
        <TabsContent value="platsnarvaro" className="mt-4">
          <SitePresence projectId={projectId} />
        </TabsContent>
        <TabsContent value="konstruktioner" className="mt-4">
          <TemporaryStructures projectId={projectId} />
        </TabsContent>
        <TabsContent value="toolbox" className="mt-4">
          <ToolboxTalks projectId={projectId} />
        </TabsContent>
        <TabsContent value="disciplin" className="mt-4">
          <DisciplinaryActions projectId={projectId} />
        </TabsContent>
        <TabsContent value="byggherrehantering" className="mt-4">
          <ByggherreHandling projectId={projectId} />
        </TabsContent>
        <TabsContent value="delegeringsordning" className="mt-4">
          <DelegationOrder projectId={projectId} />
        </TabsContent>
        <TabsContent value="delegeringsbrev" className="mt-4">
          <DelegationLetter projectId={projectId} />
        </TabsContent>
        <TabsContent value="byggherrechecklista" className="mt-4">
          <ByggherreChecklist projectId={projectId} />
        </TabsContent>
        <TabsContent value="uppgiftsfordelning" className="mt-4">
          <DutyAssignment projectId={projectId} />
        </TabsContent>
        <TabsContent value="byggherrerapport" className="mt-4">
          <ByggherreReport projectId={projectId} />
        </TabsContent>
        <TabsContent value="arbetsomrade" className="mt-4">
          <WorkAreaSurvey projectId={projectId} />
        </TabsContent>
        <TabsContent value="samordnadrisk" className="mt-4">
          <ConsolidatedRiskSummary projectId={projectId} />
        </TabsContent>
        <TabsContent value="samladarbetsomrade" className="mt-4">
          <ConsolidatedWorkAreaSurvey projectId={projectId} />
        </TabsContent>
        <TabsContent value="projektorinlamning" className="mt-4">
          <DesignerSubmissions projectId={projectId} />
        </TabsContent>
        <TabsContent value="projektorrutin" className="mt-4">
          <DesignerRoutine projectId={projectId} />
        </TabsContent>
        <TabsContent value="projektorleverans" className="mt-4">
          <DesignerDelivery projectId={projectId} />
        </TabsContent>
        <TabsContent value="forhandsanmalan" className="mt-4">
          <PriorNotification projectId={projectId} />
        </TabsContent>
        <TabsContent value="upphandling" className="mt-4">
          <ProcurementRequirements projectId={projectId} />
        </TabsContent>
        <TabsContent value="skeden" className="mt-4">
          <PhaseManager projectId={projectId} />
        </TabsContent>
        <TabsContent value="etapper" className="mt-4">
          <ProjectStages projectId={projectId} />
        </TabsContent>
        <TabsContent value="program" className="mt-4">
          <ProgramOverview projectId={projectId} />
        </TabsContent>
        <TabsContent value="sprak" className="mt-4">
          <LanguageDeliveries projectId={projectId} />
        </TabsContent>
        <TabsContent value="tidplan" className="mt-4">
          <ScheduleReview projectId={projectId} />
        </TabsContent>
        <TabsContent value="farligtmaterial" className="mt-4">
          <HazmatSurvey projectId={projectId} />
        </TabsContent>

        <TabsContent value="elsakerhet" className="mt-4">
          <ElectricalIsolations projectId={projectId} />
        </TabsContent>

        <TabsContent value="hetaarbeten" className="mt-4">
          <HotWorkPermits projectId={projectId} />
        </TabsContent>

        <TabsContent value="taplaner" className="mt-4">
          <TrafficPlans projectId={projectId} />
        </TabsContent>

        <TabsContent value="avstangningar" className="mt-4">
          <UtilityOutagePlans projectId={projectId} />
        </TabsContent>

        <TabsContent value="kvittenser" className="mt-4">
          <AcknowledgementTracking projectId={projectId} />
        </TabsContent>

        <TabsContent value="introduktion" className="mt-4">
          <SafetyInductions projectId={projectId} />
        </TabsContent>

        <TabsContent value="bevakning" className="mt-4">
          <ExpiryWatch projectId={projectId} />
        </TabsContent>

        <TabsContent value="slutdok" className="mt-4">
          <FinalDocumentation projectId={projectId} />
        </TabsContent>

        <TabsContent value="tredjeman" className="mt-4">
          <ThirdPartyPlan projectId={projectId} />
        </TabsContent>
        <TabsContent value="skyddsregler" className="mt-4">
          <SafetyRules projectId={projectId} />
        </TabsContent>
        <TabsContent value="utforande" className="mt-4">
          <ExecutionFollowup projectId={projectId} />
        </TabsContent>
        <TabsContent value="entreprenorer" className="mt-4">
          <ContractorManagement projectId={projectId} />
        </TabsContent>
        <TabsContent value="skyddsrond" className="mt-4">
          <SafetyRounds projectId={projectId} />
        </TabsContent>
        <TabsContent value="besiktningar" className="mt-4">
          <EquipmentInspections projectId={projectId} />
        </TabsContent>
        <TabsContent value="samordning" className="mt-4">
          <CoordinationMeetings projectId={projectId} />
        </TabsContent>
        <TabsContent value="skedesplan" className="mt-4">
          <StagePlan projectId={projectId} />
        </TabsContent>
        <TabsContent value="overlamning" className="mt-4">
          <HandoverDocuments projectId={projectId} />
        </TabsContent>
        <TabsContent value="qalogg" className="mt-4">
          <QaLog projectId={projectId} />
        </TabsContent>
        <TabsContent value="granskning" className="mt-4">
          <ReviewOverview projectId={projectId} />
        </TabsContent>
        <TabsContent value="startbesked" className="mt-4">
          <WorkPrepClearance projectId={projectId} />
        </TabsContent>
        <TabsContent value="kemikalier" className="mt-4">
          <SafetyDataSheetRegister projectId={projectId} />
        </TabsContent>
        <TabsContent value="scenarier" className="mt-4">
          <ScenarioAudit projectId={projectId} />
        </TabsContent>
        <TabsContent value="tidslinje" className="mt-4">
          <ProjectTimeline projectId={projectId} />
        </TabsContent>
        <TabsContent value="dagslage" className="mt-4">
          <StatusSnapshot projectId={projectId} />
        </TabsContent>
        <TabsContent value="tillbud" className="mt-4">
          <IncidentReports projectId={projectId} />
        </TabsContent>
        <TabsContent value="bruksskede" className="mt-4">
          <BruksskedeChecklists projectId={projectId} />
        </TabsContent>
        <TabsContent value="bruksuppfoljning" className="mt-4">
          <UsePhaseFollowup projectId={projectId} />
        </TabsContent>
        {isOwner && (
          <TabsContent value="behorighet" className="mt-4">
            <ProjectMembers projectId={projectId} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default BasProcessView;
